import { useEffect, useState, useCallback } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Menu,
  Loader2,
  FileText,
  User,
  DollarSign,
  CalendarClock,
  Calendar as CalendarIcon,
  Camera,
  Clock,
  Receipt,
  StickyNote,
  Star,
  MessageSquare,
  Phone,
  Mail,
  MapPin,
  Trash2,
  CheckCircle2,
  X,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import MessageBoard from "@/components/MessageBoard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTIONS = [
  { key: "info", label: "Job Information", icon: FileText },
  { key: "customer", label: "Customer Information", icon: User },
  { key: "estimate", label: "Estimate", icon: DollarSign },
  { key: "schedule", label: "Schedule", icon: CalendarClock },
  { key: "calendar", label: "Calendar", icon: CalendarIcon },
  { key: "photos", label: "Before / After Photos", icon: Camera },
  { key: "time", label: "Time / Job Tracking", icon: Clock },
  { key: "invoice", label: "Invoice / Payment", icon: Receipt },
  { key: "notes", label: "Notes", icon: StickyNote },
  { key: "review", label: "Review / Completion", icon: Star },
  { key: "messages", label: "Messages", icon: MessageSquare },
];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function SectionTitle({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC] mb-4">
      {children}
    </div>
  );
}

function Panel({ children, testid }) {
  return (
    <div className="border border-[#C9C6C6]/10 bg-[#010204] p-4 sm:p-6" data-testid={testid}>
      {children}
    </div>
  );
}

/* ---------- Schedule ---------- */
function ScheduleSection({ job, reload }) {
  const sc = job.schedule || {};
  const [date, setDate] = useState(sc.scheduled_date || "");
  const [window_, setWindow] = useState(sc.time_window || "");
  const [note, setNote] = useState(sc.note || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!date) return toast.error("Pick a date");
    setBusy(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/schedule`, {
        scheduled_date: date,
        time_window: window_ || null,
        note: note || null,
      });
      toast.success("Job scheduled — it's on your calendar");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save schedule");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/jobs/${job.id}/schedule`);
      setDate(""); setWindow(""); setNote("");
      toast.success("Schedule cleared");
      reload();
    } catch {
      toast.error("Could not clear schedule");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel testid="crm-schedule">
      <SectionTitle>Schedule</SectionTitle>
      {sc.scheduled_date && (
        <div className="mb-4 border border-[#0E93EC]/40 bg-[#0E93EC]/5 p-3 text-sm">
          <span className="text-[#EEEEEF] font-medium">{sc.scheduled_date}</span>
          {sc.time_window && <span className="text-[#C9C6C6]"> · {sc.time_window}</span>}
          {sc.note && <div className="text-xs text-[#C9C6C6] mt-1">{sc.note}</div>}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">Date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="mt-1 h-10 rounded-none border-[#C9C6C6]/15 bg-transparent text-[#EEEEEF] [color-scheme:dark]"
            data-testid="schedule-date" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">Time window (opt.)</Label>
          <Input value={window_} onChange={(e) => setWindow(e.target.value)} placeholder="e.g. 8am – 12pm"
            className="mt-1 h-10 rounded-none border-[#C9C6C6]/15 bg-transparent text-[#EEEEEF]"
            data-testid="schedule-window" />
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">Note (opt.)</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Start tear-off, bring dumpster"
          className="mt-1 h-10 rounded-none border-[#C9C6C6]/15 bg-transparent text-[#EEEEEF]"
          data-testid="schedule-note" />
      </div>
      <div className="mt-4 flex gap-2">
        <Button onClick={save} disabled={busy}
          className="rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-10 px-5"
          data-testid="schedule-save">
          {busy ? "Saving…" : "Save schedule"}
        </Button>
        {sc.scheduled_date && (
          <Button onClick={clear} disabled={busy} variant="ghost"
            className="rounded-none h-10 px-4 text-[#C9C6C6] hover:text-[#EEEEEF]"
            data-testid="schedule-clear">
            Clear
          </Button>
        )}
      </div>
    </Panel>
  );
}

/* ---------- Photos ---------- */
function PhotosSection({ job, photos, reload }) {
  const [kind, setKind] = useState("before");
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Max 3 MB per photo");
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await axios.post(`${API}/jobs/${job.id}/photos`, {
        kind,
        content_base64: b64,
        mime_type: file.type,
        filename: file.name,
      });
      toast.success("Photo added");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async (pid) => {
    try {
      await axios.delete(`${API}/jobs/${job.id}/photos/${pid}`);
      reload();
    } catch {
      toast.error("Could not delete photo");
    }
  };

  const Col = ({ k, title }) => (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-2">
        {photos.filter((p) => p.kind === k).map((p) => (
          <div key={p.id} className="relative group border border-[#C9C6C6]/10" data-testid={`photo-${k}-${p.id}`}>
            <img src={`${API}/jobs/${job.id}/photos/${p.id}/content`} alt={p.caption || title}
              className="w-full h-28 object-cover" />
            <button onClick={() => remove(p.id)}
              className="absolute top-1 right-1 bg-[#010204]/80 text-[#C9C6C6] hover:text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`photo-delete-${p.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {photos.filter((p) => p.kind === k).length === 0 && (
          <div className="col-span-2 text-xs text-[#C9C6C6] italic border border-dashed border-[#C9C6C6]/15 p-4 text-center">
            No {title.toLowerCase()} photos yet
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Panel testid="crm-photos">
      <SectionTitle>Before / After Photos</SectionTitle>
      <div className="flex items-center gap-2 mb-4">
        {["before", "after"].map((k) => (
          <button key={k} onClick={() => setKind(k)}
            className={`text-[11px] uppercase tracking-[0.2em] font-semibold px-3 py-2 border ${kind === k ? "bg-[#0E93EC] text-white border-[#0E93EC]" : "text-[#EEEEEF] border-[#C9C6C6]/20"}`}
            data-testid={`photo-kind-${k}`}>
            {k}
          </button>
        ))}
        <label className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold px-3 py-2 bg-[#0E93EC] text-white cursor-pointer hover:bg-[#0E2F5F]">
          <Camera className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Add photo"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy}
            data-testid="photo-upload-input" />
        </label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Col k="before" title="Before" />
        <Col k="after" title="After" />
      </div>
    </Panel>
  );
}

/* ---------- Time tracking ---------- */
function TimeSection({ job, entries, total, reload }) {
  const [date, setDate] = useState("");
  const [hours, setHours] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!date || !hours) return toast.error("Date and hours are required");
    setBusy(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/time`, {
        work_date: date,
        hours: Number(hours),
        note: note || null,
      });
      setDate(""); setHours(""); setNote("");
      toast.success("Time logged");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log time");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/jobs/${job.id}/time/${id}`).catch(() => {});
    reload();
  };

  return (
    <Panel testid="crm-time">
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Time / Job Tracking</SectionTitle>
        <div className="text-sm text-[#EEEEEF] font-medium" data-testid="time-total">
          Total: <span className="text-[#0E93EC]">{total} h</span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="h-10 rounded-none border-[#C9C6C6]/15 bg-transparent text-[#EEEEEF] [color-scheme:dark]"
          data-testid="time-date" />
        <Input type="number" min="0.5" max="24" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)}
          placeholder="Hours"
          className="h-10 rounded-none border-[#C9C6C6]/15 bg-transparent text-[#EEEEEF]"
          data-testid="time-hours" />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (opt.)"
          className="h-10 rounded-none border-[#C9C6C6]/15 bg-transparent text-[#EEEEEF] col-span-2 sm:col-span-1"
          data-testid="time-note" />
        <Button onClick={add} disabled={busy}
          className="rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-10"
          data-testid="time-add">
          <Plus className="w-4 h-4 mr-1" /> Log
        </Button>
      </div>
      <div className="space-y-1.5">
        {entries.map((t) => (
          <div key={t.id} className="flex items-center justify-between border border-[#C9C6C6]/10 bg-[#0D1421] px-3 py-2 text-sm"
            data-testid={`time-entry-${t.id}`}>
            <span className="text-[#EEEEEF]">{t.work_date} <span className="text-[#0E93EC] font-medium">{t.hours}h</span>
              {t.note && <span className="text-[#C9C6C6]"> · {t.note}</span>}
            </span>
            <button onClick={() => remove(t.id)} className="text-[#C9C6C6] hover:text-white" data-testid={`time-delete-${t.id}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {entries.length === 0 && (
          <div className="text-xs text-[#C9C6C6] italic">No time logged yet.</div>
        )}
      </div>
    </Panel>
  );
}

/* ---------- Line-items editor (shared by invoice) ---------- */
export function LineItemsEditor({ items, setItems }) {
  const update = (i, field, value) => {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [field]: value } : it)));
  };
  const total = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  return (
    <div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <Input value={it.description} onChange={(e) => update(i, "description", e.target.value)}
              placeholder="Description (e.g. Labor, Materials)"
              className="col-span-12 sm:col-span-6 h-10 rounded-none border-[#C9C6C6]/15 bg-transparent text-[#EEEEEF]"
              data-testid={`li-desc-${i}`} />
            <Input type="number" min="0" step="0.5" value={it.qty} onChange={(e) => update(i, "qty", e.target.value)}
              placeholder="Qty"
              className="col-span-4 sm:col-span-2 h-10 rounded-none border-[#C9C6C6]/15 bg-transparent text-[#EEEEEF]"
              data-testid={`li-qty-${i}`} />
            <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => update(i, "unit_price", e.target.value)}
              placeholder="Unit $"
              className="col-span-4 sm:col-span-2 h-10 rounded-none border-[#C9C6C6]/15 bg-transparent text-[#EEEEEF]"
              data-testid={`li-price-${i}`} />
            <div className="col-span-3 sm:col-span-1 text-sm text-[#C9C6C6] text-right">
              {money((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}
            </div>
            <button onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
              className="col-span-1 text-[#C9C6C6] hover:text-white justify-self-end"
              data-testid={`li-remove-${i}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <Button onClick={() => setItems((p) => [...p, { description: "", qty: 1, unit_price: "" }])}
          variant="ghost" className="rounded-none h-9 px-3 text-[#0E93EC] hover:text-white text-xs uppercase tracking-[0.2em]"
          data-testid="li-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add line
        </Button>
        <div className="text-[#EEEEEF] font-medium" data-testid="li-total">
          Total: <span className="text-[#0E93EC] font-editorial text-xl">{money(total)}</span>
        </div>
      </div>
    </div>
  );
}

/* ---------- Invoice ---------- */
function InvoiceSection({ job, invoice, reload }) {
  const [items, setItems] = useState([{ description: "", qty: 1, unit_price: "" }]);
  const [note, setNote] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (invoice) {
      setItems(invoice.items.map((i) => ({ description: i.description, qty: i.qty, unit_price: i.unit_price })));
      setNote(invoice.note || "");
      setDue(invoice.due_date || "");
    }
  }, [invoice]);

  const valid = items.filter((i) => i.description.trim() && Number(i.qty) > 0 && Number(i.unit_price) >= 0);

  const save = async () => {
    if (valid.length === 0) return toast.error("Add at least one line item");
    setBusy(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/invoice`, {
        items: valid.map((i) => ({ description: i.description.trim(), qty: Number(i.qty), unit_price: Number(i.unit_price) })),
        note: note || null,
        due_date: due || null,
      });
      toast.success("Invoice saved");
      setEditing(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save invoice");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/invoice/status`, { status });
      toast.success(status === "sent" ? "Invoice sent to homeowner" : status === "paid" ? "Marked as paid" : "Moved back to draft");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status");
    } finally {
      setBusy(false);
    }
  };

  const statusBadge = {
    draft: "bg-[#C9C6C6]/20 text-[#EEEEEF]",
    sent: "bg-[#0E93EC] text-white",
    paid: "bg-[#0E2F5F] text-white border border-[#0E93EC]",
  };

  return (
    <Panel testid="crm-invoice">
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Invoice / Payment Tracking</SectionTitle>
        {invoice && (
          <span className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 ${statusBadge[invoice.status]}`}
            data-testid="invoice-status">
            {invoice.status}
          </span>
        )}
      </div>

      {invoice && !editing ? (
        <div>
          <div className="space-y-1.5 mb-3">
            {invoice.items.map((i, idx) => (
              <div key={idx} className="flex justify-between text-sm border-b border-[#C9C6C6]/10 pb-1.5">
                <span className="text-[#EEEEEF]">{i.description} <span className="text-[#C9C6C6]">× {i.qty}</span></span>
                <span className="text-[#EEEEEF]">{money(i.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center mb-3">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">Total</span>
            <span className="font-editorial text-2xl text-[#EEEEEF]" data-testid="invoice-total">{money(invoice.total)}</span>
          </div>
          {(invoice.note || invoice.due_date) && (
            <div className="text-xs text-[#C9C6C6] mb-4">
              {invoice.due_date && <span>Due: {invoice.due_date} </span>}
              {invoice.note && <span>· {invoice.note}</span>}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {invoice.status === "draft" && (
              <>
                <Button onClick={() => setStatus("sent")} disabled={busy}
                  className="rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-10 px-5"
                  data-testid="invoice-send">
                  Send to homeowner
                </Button>
                <Button onClick={() => setEditing(true)} variant="ghost"
                  className="rounded-none h-10 px-4 text-[#C9C6C6] hover:text-[#EEEEEF]"
                  data-testid="invoice-edit">
                  Edit
                </Button>
              </>
            )}
            {invoice.status === "sent" && (
              <Button onClick={() => setStatus("paid")} disabled={busy}
                className="rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-10 px-5"
                data-testid="invoice-paid">
                Mark as paid
              </Button>
            )}
            {invoice.status === "paid" && (
              <div className="text-sm text-[#0E93EC] flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Paid{invoice.paid_at ? ` · ${invoice.paid_at.slice(0, 10)}` : ""}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div>
          <LineItemsEditor items={items} setItems={setItems} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">Due date (opt.)</Label>
              <Input type="date" value={due} onChange={(e) => setDue(e.target.value)}
                className="mt-1 h-10 rounded-none border-[#C9C6C6]/15 bg-transparent text-[#EEEEEF] [color-scheme:dark]"
                data-testid="invoice-due" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">Note (opt.)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Net 7"
                className="mt-1 h-10 rounded-none border-[#C9C6C6]/15 bg-transparent text-[#EEEEEF]"
                data-testid="invoice-note" />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={save} disabled={busy}
              className="rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-10 px-5"
              data-testid="invoice-save">
              {busy ? "Saving…" : invoice ? "Save changes" : "Create invoice"}
            </Button>
            {invoice && (
              <Button onClick={() => setEditing(false)} variant="ghost"
                className="rounded-none h-10 px-4 text-[#C9C6C6] hover:text-[#EEEEEF]">
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}

/* ---------- Notes ---------- */
function NotesSection({ job, notes, reload }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/notes`, { text: text.trim() });
      setText("");
      reload();
    } catch {
      toast.error("Could not save note");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    await axios.delete(`${API}/jobs/${job.id}/notes/${id}`).catch(() => {});
    reload();
  };
  return (
    <Panel testid="crm-notes">
      <SectionTitle>Job Notes</SectionTitle>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
        placeholder="Gate code is 4321. Dog in yard. Call before arrival."
        className="bg-[#0D1421] border-[#C9C6C6]/15 rounded-none text-[#EEEEEF]"
        data-testid="note-input" />
      <Button onClick={add} disabled={busy || !text.trim()}
        className="mt-3 rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-10 px-5"
        data-testid="note-add">
        Add note
      </Button>
      <div className="mt-5 space-y-2">
        {notes.map((n) => (
          <div key={n.id} className="flex items-start justify-between gap-3 bg-[#0D1421] border-l-2 border-[#0E93EC] p-3"
            data-testid={`note-${n.id}`}>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9C6C6] mb-1">{(n.created_at || "").slice(0, 10)}</div>
              <p className="text-sm text-[#EEEEEF] whitespace-pre-wrap">{n.text}</p>
            </div>
            <button onClick={() => remove(n.id)} className="text-[#C9C6C6] hover:text-white shrink-0" data-testid={`note-delete-${n.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {notes.length === 0 && <div className="text-xs text-[#C9C6C6] italic">No notes on this job yet.</div>}
      </div>
    </Panel>
  );
}

/* ---------- Mini calendar (this job highlighted) ---------- */
function MiniCalendar({ job }) {
  const sc = job.schedule?.scheduled_date;
  const base = sc ? new Date(sc + "T12:00:00") : new Date();
  const [cursor] = useState(base);
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startPad = first.getDay();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const iso = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  return (
    <Panel testid="crm-calendar">
      <SectionTitle>Calendar</SectionTitle>
      <div className="text-sm text-[#EEEEEF] font-medium mb-3">
        {cursor.toLocaleString("en-US", { month: "long", year: "numeric" })}
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-[#C9C6C6] mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => (
          <div key={i}
            className={`aspect-square flex items-center justify-center text-xs ${
              d && iso(d) === sc
                ? "bg-[#0E93EC] text-white font-bold"
                : d
                  ? "text-[#EEEEEF]/70 border border-[#C9C6C6]/10"
                  : ""
            }`}
            data-testid={d && iso(d) === sc ? "calendar-job-day" : undefined}>
            {d || ""}
          </div>
        ))}
      </div>
      <Link to="/workspace/calendar"
        className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0E93EC] hover:text-white"
        data-testid="crm-open-full-calendar">
        Open full calendar <CalendarIcon className="w-3.5 h-3.5" />
      </Link>
    </Panel>
  );
}

/* ---------- Main page ---------- */
export default function ProCRM() {
  const { jobId } = useParams();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [section, setSection] = useState("info");
  const [menuOpen, setMenuOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}/crm`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load job record");
    } finally {
      setBusy(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (loading || busy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1421] text-[#C9C6C6]">
        <Loader2 className="w-5 h-5 animate-spin text-[#0E93EC] mr-2" /> Loading job record…
      </div>
    );
  }
  if (!user) return <Navigate to="/workspace/login" replace />;
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0D1421] text-[#C9C6C6] p-12 text-center">
        Job record not found.{" "}
        <Link to="/workspace" className="text-[#0E93EC] underline">Back to workspace</Link>
      </div>
    );
  }

  const { job, lead, estimate, photos, time_entries, total_hours, notes, invoice, review } = data;
  const active = SECTIONS.find((s) => s.key === section);

  const sign = async () => {
    try {
      await axios.post(`${API}/jobs/${job.id}/signoff`, { party: "contractor" });
      toast.success("Signed off. Job completes once the homeowner signs too.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign off");
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1421]" data-testid="crm-page">
      {/* Sub-header */}
      <header className="border-b border-[#C9C6C6]/10 bg-[#010204] sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setMenuOpen(true)}
              className="p-2 border border-[#C9C6C6]/20 text-[#EEEEEF] hover:border-[#0E93EC] hover:text-[#0E93EC] transition-colors"
              data-testid="crm-menu-toggle" aria-label="Open job menu">
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/workspace" className="p-2 text-[#C9C6C6] hover:text-[#EEEEEF]" data-testid="crm-back">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#0E93EC] font-semibold">
                W/O {job.invoice_number}
              </div>
              <div className="font-editorial text-lg text-[#EEEEEF] tracking-tight truncate">
                {lead?.service_type || "Job"} · {lead?.name || ""}
              </div>
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 bg-[#0E93EC]/15 text-[#0E93EC] border border-[#0E93EC]/30 shrink-0"
            data-testid="crm-job-status">
            {(job.status || "").replace("_", " ")}
          </span>
        </div>
      </header>

      {/* Hamburger drawer */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-72 bg-[#010204] border-r border-[#C9C6C6]/10 p-0" data-testid="crm-menu">
          <SheetHeader className="p-5 border-b border-[#C9C6C6]/10 text-left">
            <SheetTitle className="text-[11px] uppercase tracking-[0.25em] text-[#0E93EC] font-semibold">
              W/O {job.invoice_number}
            </SheetTitle>
          </SheetHeader>
          <nav className="py-2">
            {SECTIONS.map((s) => (
              <button key={s.key}
                onClick={() => { setSection(s.key); setMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-5 py-3 text-sm transition-colors ${
                  section === s.key
                    ? "bg-[#0E93EC]/10 text-[#0E93EC] border-l-2 border-[#0E93EC]"
                    : "text-[#EEEEEF]/80 hover:bg-[#EEEEEF]/5 border-l-2 border-transparent"
                }`}
                data-testid={`crm-nav-${s.key}`}>
                <s.icon className="w-4 h-4 shrink-0" />
                {s.label}
              </button>
            ))}
          </nav>
        </SheetContent>
      </Sheet>

      {/* Content */}
      <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-editorial text-3xl font-light text-[#EEEEEF] tracking-tight mb-6" data-testid="crm-section-title">
          {active.label}
        </h1>

        {section === "info" && (
          <Panel testid="crm-info">
            <SectionTitle>Job Information</SectionTitle>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
              <div><dt className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">W/O number</dt>
                <dd className="text-[#EEEEEF] font-mono-alt">{job.invoice_number}</dd></div>
              <div><dt className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Status</dt>
                <dd className="text-[#EEEEEF] capitalize">{(job.status || "").replace("_", " ")}</dd></div>
              <div><dt className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Service</dt>
                <dd className="text-[#EEEEEF]">{lead?.service_type}</dd></div>
              <div><dt className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Accepted</dt>
                <dd className="text-[#EEEEEF]">{(job.accepted_at || "").slice(0, 10) || "—"}</dd></div>
              <div><dt className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Scheduled</dt>
                <dd className="text-[#EEEEEF]">{job.schedule?.scheduled_date || "Not scheduled"}{job.schedule?.time_window ? ` · ${job.schedule.time_window}` : ""}</dd></div>
              <div><dt className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Estimate total</dt>
                <dd className="text-[#EEEEEF]">{money(job.price_low)}{job.price_high > job.price_low ? ` – ${money(job.price_high)}` : ""}</dd></div>
            </dl>
            {job.scope && (
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Scope of work</div>
                <p className="text-sm text-[#EEEEEF] whitespace-pre-wrap leading-relaxed">{job.scope}</p>
              </div>
            )}
            {lead?.project_details && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Homeowner's request</div>
                <p className="text-sm text-[#C9C6C6] whitespace-pre-wrap leading-relaxed">{lead.project_details}</p>
              </div>
            )}
          </Panel>
        )}

        {section === "customer" && (
          <Panel testid="crm-customer">
            <SectionTitle>Customer Information</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a href={`tel:${lead?.phone}`} className="border border-[#C9C6C6]/15 p-4 hover:border-[#0E93EC] transition-colors" data-testid="crm-customer-call">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Call</div>
                <div className="flex items-center gap-2 text-[#EEEEEF] font-medium">
                  <Phone className="w-4 h-4 text-[#0E93EC]" /> {lead?.phone || "—"}
                </div>
              </a>
              <a href={`mailto:${lead?.email}`} className="border border-[#C9C6C6]/15 p-4 hover:border-[#0E93EC] transition-colors" data-testid="crm-customer-email">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Email</div>
                <div className="flex items-center gap-2 text-[#EEEEEF] font-medium truncate">
                  <Mail className="w-4 h-4 text-[#0E93EC]" /> {lead?.email || "—"}
                </div>
              </a>
              <div className="border border-[#C9C6C6]/15 p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Name</div>
                <div className="text-[#EEEEEF] font-medium">{lead?.name || "—"}</div>
              </div>
              <div className="border border-[#C9C6C6]/15 p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Location</div>
                <div className="flex items-center gap-2 text-[#EEEEEF] font-medium">
                  <MapPin className="w-4 h-4 text-[#0E93EC]" /> {lead?.address ? `${lead.address}, ` : ""}{lead?.zip_code}
                </div>
              </div>
            </div>
          </Panel>
        )}

        {section === "estimate" && (
          <Panel testid="crm-estimate">
            <SectionTitle>Estimate</SectionTitle>
            {estimate ? (
              <div>
                {estimate.items ? (
                  <div className="space-y-1.5 mb-3">
                    {estimate.items.map((i, idx) => (
                      <div key={idx} className="flex justify-between text-sm border-b border-[#C9C6C6]/10 pb-1.5">
                        <span className="text-[#EEEEEF]">{i.description} <span className="text-[#C9C6C6]">× {i.qty}</span></span>
                        <span className="text-[#EEEEEF]">{money(i.line_total)}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="flex justify-between items-center">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">
                    {estimate.items ? "Total" : "Estimated range"}
                  </span>
                  <span className="font-editorial text-2xl text-[#EEEEEF]" data-testid="crm-estimate-total">
                    {money(estimate.price_low)}{estimate.price_high > estimate.price_low ? ` – ${money(estimate.price_high)}` : ""}
                  </span>
                </div>
                <p className="mt-3 text-sm text-[#C9C6C6] whitespace-pre-wrap">{estimate.scope}</p>
                <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#0E93EC] font-semibold">
                  Status: {estimate.status}
                </div>
              </div>
            ) : (
              <div className="text-sm text-[#C9C6C6] italic">No estimate on file for this job.</div>
            )}
          </Panel>
        )}

        {section === "schedule" && <ScheduleSection job={job} reload={load} />}
        {section === "calendar" && <MiniCalendar job={job} />}
        {section === "photos" && <PhotosSection job={job} photos={photos} reload={load} />}
        {section === "time" && <TimeSection job={job} entries={time_entries} total={total_hours} reload={load} />}
        {section === "invoice" && <InvoiceSection job={job} invoice={invoice} reload={load} />}
        {section === "notes" && <NotesSection job={job} notes={notes} reload={load} />}

        {section === "review" && (
          <Panel testid="crm-review">
            <SectionTitle>Review / Completion</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div className={`p-3 border ${job.agreement_contractor_signed ? "border-[#0E93EC] bg-[#0E93EC]/5" : "border-[#C9C6C6]/10"}`}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Your sign-off</div>
                <div className="text-sm text-[#EEEEEF]">{job.agreement_contractor_signed ? "Signed off" : "Not signed"}</div>
              </div>
              <div className={`p-3 border ${job.agreement_homeowner_signed ? "border-[#0E93EC] bg-[#0E93EC]/5" : "border-[#C9C6C6]/10"}`}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Homeowner sign-off</div>
                <div className="text-sm text-[#EEEEEF]">{job.agreement_homeowner_signed ? "Signed off" : "Not signed"}</div>
              </div>
            </div>
            {job.status !== "completed" && !job.agreement_contractor_signed && (
              <Button onClick={sign}
                className="rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-10 px-5"
                data-testid="crm-signoff">
                Mark my side complete
              </Button>
            )}
            {job.status === "completed" && (
              <div className="text-sm text-[#0E93EC] flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-4 h-4" /> Job completed
              </div>
            )}
            {review ? (
              <div className="border-l-2 border-[#0E93EC] bg-[#0D1421] px-3 py-2 mt-4">
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC] mb-1">
                  Homeowner review · {review.rating}★ · {review.status}
                </div>
                <p className="text-sm text-[#EEEEEF] whitespace-pre-wrap">{review.body}</p>
              </div>
            ) : (
              <div className="text-xs text-[#C9C6C6] italic mt-4">No review yet — the homeowner is prompted after completion.</div>
            )}
          </Panel>
        )}

        {section === "messages" && (
          <div data-testid="crm-messages">
            <MessageBoard jobId={job.id} viewerRole="contractor" jobStatus={job.status} onRead={() => {}} />
          </div>
        )}
      </main>
    </div>
  );
}
